enum AdminEnvironment {
  STAGING = 'staging',
  PRODUCTION = 'production',
}

const AWS_REGION = 'af-south-1';
const AWS_ACCOUNT_ID = '581281163086';

const RESOURCE_PREFIX_BY_ENV: Record<AdminEnvironment, string> = {
  [AdminEnvironment.STAGING]: 'stg-v7-tf',
  [AdminEnvironment.PRODUCTION]: 'prod-v7-tf',
};

export const currentEnvironment =
  import.meta.env.VITE_ENVIRONMENT === AdminEnvironment.STAGING
    ? AdminEnvironment.STAGING
    : AdminEnvironment.PRODUCTION;

const resourcePrefix = RESOURCE_PREFIX_BY_ENV[currentEnvironment];

export const CLOUDWATCH_REGION = AWS_REGION;
export const UPLOADS_BUCKET = `${resourcePrefix}-uploads-${AWS_ACCOUNT_ID}-${AWS_REGION}`;
export const OUTPUTS_BUCKET = `${resourcePrefix}-legal-opinion-outputs-bucket-${AWS_ACCOUNT_ID}-${AWS_REGION}`;
export const LAMBDA_LOG_GROUP = `/aws/lambda/${resourcePrefix}-legal-ai-api`;

export const getS3UploadsConsoleUrl = (userId: string): string =>
  `https://${AWS_REGION}.console.aws.amazon.com/s3/buckets/${UPLOADS_BUCKET}?prefix=uploads/${userId}/&region=${AWS_REGION}`;

export const getS3OutputConsoleUrl = (outputLocation: string): string =>
  `https://${AWS_REGION}.console.aws.amazon.com/s3/object/${OUTPUTS_BUCKET}?prefix=${outputLocation}&region=${AWS_REGION}`;

const stripExtension = (key: string): string => key.replace(/\.[^.]+$/, '');

export const getS3OcrInputConsoleUrl = (outputLocation: string): string =>
  `https://${AWS_REGION}.console.aws.amazon.com/s3/object/${OUTPUTS_BUCKET}?prefix=${stripExtension(outputLocation)}_ocr-input.txt&region=${AWS_REGION}`;

export const getS3AiResponseConsoleUrl = (outputLocation: string): string =>
  `https://${AWS_REGION}.console.aws.amazon.com/s3/object/${OUTPUTS_BUCKET}?prefix=${stripExtension(outputLocation)}_ai-response.txt&region=${AWS_REGION}`;

const CLOUDWATCH_OBJECT_SEPARATOR = '$257E';
const CLOUDWATCH_ARRAY_START = '$2528';
const CLOUDWATCH_STRING_PREFIX = '$2527';
const CLOUDWATCH_OBJECT_END = '$2529';

enum CloudWatchTimeType {
  ABSOLUTE = 'ABSOLUTE',
}

enum CloudWatchTimeZone {
  LOCAL = 'Local',
}

const encodeCloudWatchString = (value: string): string =>
  value
    .split('')
    .map((character) =>
      /^[a-zA-Z0-9-.]$/.test(character)
        ? character
        : `*${character.charCodeAt(0).toString(16).padStart(2, '0')}`
    )
    .join('');

const encodeCloudWatchArray = (values: string[]): string =>
  [
    CLOUDWATCH_OBJECT_SEPARATOR,
    CLOUDWATCH_ARRAY_START,
    ...values.flatMap((value) => [
      CLOUDWATCH_OBJECT_SEPARATOR,
      CLOUDWATCH_STRING_PREFIX,
      encodeCloudWatchString(value),
    ]),
  ].join('');

const encodeCloudWatchObject = (
  payload: Record<string, string | number | boolean | string[]>,
  prefix: string[] = [CLOUDWATCH_OBJECT_SEPARATOR, CLOUDWATCH_ARRAY_START]
): string => {
  const output: string[] = [];

  Object.entries(payload).forEach(([key, value], index) => {
    if (index === 0) {
      output.push(...prefix, key);
    } else {
      output.push(CLOUDWATCH_OBJECT_SEPARATOR, key);
    }

    if (typeof value === 'boolean' || typeof value === 'number') {
      output.push(CLOUDWATCH_OBJECT_SEPARATOR, String(value));
      return;
    }

    if (Array.isArray(value)) {
      output.push(encodeCloudWatchArray(value));
      return;
    }

    output.push(
      CLOUDWATCH_OBJECT_SEPARATOR,
      CLOUDWATCH_STRING_PREFIX,
      encodeCloudWatchString(value)
    );
  });

  return output.join('');
};

export const getCloudWatchLogsUrl = (jobId: string, createdAt: string): string => {
  const createdAtMs = new Date(createdAt).getTime();
  const start = new Date(createdAtMs - 5 * 60 * 1000).toISOString();
  const end = new Date(createdAtMs + 60 * 60 * 1000).toISOString();
  const editorString = [
    'fields @timestamp, @requestId, @message',
    `| filter @message like /${jobId}/`,
    '| sort @timestamp asc',
    '| limit 200',
  ].join('\n');

  const queryDetail = [
    encodeCloudWatchObject({
      editorString,
      start,
      end,
      timeType: CloudWatchTimeType.ABSOLUTE,
      tz: CloudWatchTimeZone.LOCAL,
      isLiveTail: false,
    }),
    encodeCloudWatchObject(
      {
        source: [LAMBDA_LOG_GROUP],
      },
      [CLOUDWATCH_OBJECT_SEPARATOR]
    ),
    CLOUDWATCH_OBJECT_END,
    CLOUDWATCH_OBJECT_END,
  ].join('');

  return `https://${CLOUDWATCH_REGION}.console.aws.amazon.com/cloudwatch/home?region=${CLOUDWATCH_REGION}#logsV2:logs-insights$3FqueryDetail$3D${queryDetail}`;
};
